import { useCallback, useEffect, useRef, useState } from 'react';
import { getKbAuditApplyStatus } from '../services/apiService';

const STATUS_CHECK_INTERVAL_MS = 60_000;
const MAX_AUTOMATIC_CHECKS = 6;

type SyncKind = 'updating' | 'checking' | 'live' | 'delayed' | 'error';

export interface SyncState {
  kind: SyncKind;
  findingIds: string[];
  automaticChecks: number;
  message?: string;
}

export const useKbAuditLiveSync = () => {
  const [state, setState] = useState<SyncState | null>(null);
  const inFlight = useRef(false);
  const generation = useRef(0);

  const begin = useCallback((findingIds: string[]) => {
    generation.current += 1;
    inFlight.current = false;
    setState((current) => ({
      kind: 'updating',
      findingIds: [...new Set([
        ...(current && current.kind !== 'live' ? current.findingIds : []),
        ...findingIds,
      ])],
      automaticChecks: 0,
    }));
  }, []);

  const dismiss = useCallback(() => {
    generation.current += 1;
    inFlight.current = false;
    setState(null);
  }, []);

  const check = useCallback(async (automatic: boolean) => {
    if (!state || state.kind === 'live' || state.kind === 'checking' || inFlight.current) return;
    inFlight.current = true;
    const activeGeneration = generation.current;
    setState({ ...state, kind: 'checking' });

    try {
      const response = await getKbAuditApplyStatus(state.findingIds);
      if (activeGeneration !== generation.current) return;
      const items = response?.items || [];
      const statuses = new Map(items.map((item) => [item.findingId, item]));
      const hasMissingStatus = state.findingIds.some((id) => !statuses.has(id));
      const hasRuntimeError = items.some((item) => item.status === 'error');
      const automaticChecks = state.automaticChecks + Number(automatic);

      if (hasMissingStatus) {
        setState({ ...state, kind: 'error', message: 'We could not confirm every change. Check again shortly.' });
      } else if (hasRuntimeError) {
        setState({ ...state, kind: 'error', message: 'Live guidance could not update. Check again or contact support.' });
      } else if (state.findingIds.every((id) => statuses.get(id)?.status === 'live')) {
        setState({ ...state, kind: 'live', automaticChecks });
      } else {
        setState({
          ...state,
          kind: automaticChecks >= MAX_AUTOMATIC_CHECKS ? 'delayed' : 'updating',
          automaticChecks,
        });
      }
    } catch {
      if (activeGeneration === generation.current) {
        setState({ ...state, kind: 'error', message: 'We could not check live guidance. Your changes are saved. Try again.' });
      }
    } finally {
      if (activeGeneration === generation.current) inFlight.current = false;
    }
  }, [state]);

  useEffect(() => {
    if (state?.kind !== 'updating') return undefined;
    const timer = window.setTimeout(() => { void check(true); }, STATUS_CHECK_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [state, check]);

  useEffect(() => () => { generation.current += 1; }, []);

  const checkNow = useCallback(() => check(false), [check]);
  return { state, begin, checkNow, dismiss };
};
